import React, { useState, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import ChartInfoModal from '../../../common/data_display/ChartInfoModal';
import InfoButton from '../../../common/data_display/InfoButton';
import { ResponsiveLine } from '@nivo/line';
import cloneDeep from 'lodash/cloneDeep';
import { secondaryMain, grey } from '../../../constants/colors';

const useStyles = makeStyles((theme) => ({
  content: {
    paddingTop: 12,
  },
  title: {
    paddingBottom: 2,
    color: secondaryMain,
  },
  inlineTitle: {
    display: 'flex',
    alignItems: 'center',
  },
  infoButton: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  legend: {
    backgroundColor: "#eff2f6",
    border: `1.95px solid ${grey}`,
    borderRadius: "2px",
    padding: "5px",
    display: "inline-flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "15px",
    margin: "auto",
    marginTop: "15px",
  },
  legendItem: {
    cursor: 'pointer',
    alignItems: 'center',
    '& span': {
      fontSize: '0.6rem',
      fontWeight: 'bold',
    },
    '&:hover': {
      backgroundColor: "rgba(0, 0, 0, 0.12)",
      borderRadius: '4px',
    }
  }
}));

// Convert interval/value format to x/y format for Nivo
// Preserve the date field for tooltip display
function convertLinesXY(lines) {
  let linesCopy = cloneDeep(lines);
  for (let i = 0; i < linesCopy.length; ++i) {
    linesCopy[i].data = linesCopy[i].data.map((item) => ({
      x: item.interval,
      y: item.value,
      date: item.date // Preserve the date for tooltip
    }));
  }
  return linesCopy;
}

// Find the max Y value in the data
function getMaxDataPoint(lines) {
  let maxDataPoint = 0;
  for (let i = 0; i < lines.length; ++i) {
    let temp = Math.max(...lines[i].data.map(item => item.y));
    if (temp > maxDataPoint) {
      maxDataPoint = temp;
    }
  }
  return maxDataPoint;
}

// Find the min/max X (interval) values
function getMinInterval(lines) {
  let minInterval = Infinity;
  for (let i = 0; i < lines.length; ++i) {
    if (lines[i].data && lines[i].data.length > 0) {
      let temp = Math.min(...lines[i].data.map(item => item.x));
      if (temp < minInterval) {
        minInterval = temp;
      }
    }
  }
  return minInterval;
}

function getMaxInterval(lines) {
  let maxInterval = -Infinity;
  for (let i = 0; i < lines.length; ++i) {
    if (lines[i].data && lines[i].data.length > 0) {
      let temp = Math.max(...lines[i].data.map(item => item.x));
      if (temp > maxInterval) {
        maxInterval = temp;
      }
    }
  }
  return maxInterval;
}

// Converts D0 & D2 intervals to Line format to plot on the chart
function D0D2_toLine(d0_interval, d2_interval, maxYValue, scale) {
  let intervalLines = [];
  if (d0_interval) {
    intervalLines.push({
      id: "Vert D0",
      data: [{x: d0_interval, y: 0}, {x: d0_interval, y: maxYValue * scale}],
      strokeStyle: 'vertical',
      color: "black"
    });
  }
  if (d2_interval) {
    intervalLines.push({
      id: "Vert D2",
      data: [{x: d2_interval, y: 0}, {x: d2_interval, y: maxYValue * scale}],
      strokeStyle: 'vertical',
      color: "black"
    });
  }
  return intervalLines;
}

export default function AdmNivoChart(props) {
  const [showCrosshair, setShowCrosshair] = useState(true);
  const [crosshairType, setCrosshairType] = useState('bottom-left');
  const [modalOpen, setModalOpen] = React.useState(false);
  const handleModalOpen = () => setModalOpen(true);
  const handleModalClose = () => setModalOpen(false);

  const classes = useStyles();
  const { title, lines, rects, scatters, chartHeight, allowHidingLines, allowHoverTooltip, xAxisLabel, d0_interval, d2_interval } = props;

  const safeLines = lines ? convertLinesXY(lines) : [];
  const safeRects = rects ? convertLinesXY(rects) : [];
  const safeScatters = scatters ? convertLinesXY(scatters) : [];

  const maxYValue = getMaxDataPoint(safeLines);
  const minInterval = getMinInterval(safeLines);
  const maxInterval = getMaxInterval(safeLines);

  // Add D0/D2 vertical lines
  const verticalLines = D0D2_toLine(d0_interval, d2_interval, maxYValue, 1.1);
  const allLines = safeLines.concat(verticalLines).concat(safeRects).concat(safeScatters);

  const legendData = safeLines.filter((line) => line.legend).sort((a, b) => (a?.keyOrder ?? 1) - (b?.keyOrder ?? 1));

  // Handles hiding/showing lines when clicking on the legend items
  const [hiddenLines, setHiddenLines] = useState([]);
  const safePlottableData = useMemo(
    () => allLines.filter((item) => !hiddenLines.includes(item.id)),
    [hiddenLines, allLines]
  );

  // Handles the different line styles (dashed, dotted, solid, vertical)
  function strokeType(strokeStyle) {
    if (strokeStyle === "dashed") {
      return { strokeDasharray: "6, 3", strokeWidth: 2 };
    } else if (strokeStyle === "dotted") {
      return { strokeDasharray: "3, 6", strokeWidth: 3 };
    } else if (strokeStyle === "vertical") {
      return { strokeWidth: 1 };
    }
    return { strokeWidth: 2 };
  }

  // Develops the Dashed, Dotted, and Solid Lines
  const DashedSolidLine = ({ series, lineGenerator, xScale, yScale }) => {
    return series
      .filter(({ id }) => !id.includes("Vert"))
      .map(({ id, color, fill, strokeStyle, legend, points, data }) => (
        <React.Fragment key={id}>
          <path
            d={lineGenerator(
              data.map((d) => ({
                x: xScale(d.data.x),
                y: yScale(d.data.y),
              }))
            )}
            stroke={color}
            style={strokeType(strokeStyle)}
            fill="none"
          />
        </React.Fragment>
      ));
  };

  // Draw the vertical lines for D0 and D2
  const VerticalLines = ({ series, lineGenerator, xScale, yScale }) => {
    return series
      .filter(({ id }) => id.includes("Vert"))
      .map(({ id, color, strokeStyle, data }) => (
        <path
          key={id}
          d={lineGenerator(
            data.map((d) => ({
              x: xScale(d.data.x),
              y: yScale(d.data.y),
            }))
          )}
          stroke={color}
          style={strokeType(strokeStyle)}
          fill="none"
        />
      ));
  };

  // D0 & D2 Marker to indicate what each vertical line represents
  const D0D2Marker = ({ series, xScale, yScale }) => {
    return series
      .filter(({ id }) => id.includes("Vert"))
      .map(({ id, color, strokeStyle, legend, points, data }) => (
        <g key={id} transform={`translate(${data[1].position.x},${data[1].position.y})`}>
          <rect
            x={-10}
            y={-7}
            width={20}
            height={15}
            fill="#d4d7de"
            stroke="black"
            strokeWidth={1}
            rx={2}
            ry={2}
          />
          <text
            x={0}
            y={0}
            fill="black"
            fontSize={10}
            fontWeight="bold"
            textAnchor="middle"
            alignmentBaseline="central"
          >
            {String(id).replace("Vert ", "")}
          </text>
        </g>
      ));
  };

  // Custom Layer to draw Rectangles onto line chart
  const RectLayer = ({ series, xScale, yScale }) => {
    return series
      .filter(({ id }) => id.includes("Rect"))
      .map(({ id, color, opacity, data }) => (
        <rect
          key={id}
          x={data[0].position.x}
          y={data[1].position.y}
          width={Math.abs(data[1].position.x - data[0].position.x)}
          height={Math.abs(data[1].position.y - data[0].position.y)}
          fill={color}
          opacity={opacity}
          style={{ pointerEvents: 'none' }}
        />
      ));
  };

  // Custom Shape for the Legend
  const LegendLineShape = ({ x, y, size, fill, id }) => {
    const dataItem = safePlottableData.find(item => item.id === id);
    const strokeDasharray = dataItem && (dataItem.strokeStyle === 'dashed' || dataItem.strokeStyle === 'dotted') ? '4, 4' : 'none';

    return (
      <line
        x1={x}
        y1={y + size / 2}
        x2={x + size}
        y2={y + size / 2}
        stroke={fill}
        strokeWidth={4}
        strokeDasharray={strokeDasharray}
        style={{ pointerEvents: 'none' }}
      />
    );
  };

  // Custom Legend
  const CustomLegend = ({ legendData }) => {
    return (
      <div className={`${classes.legend}`}>
        {legendData.map(line => (
          <div
            key={line.id}
            className={classes.legendItem}
            onClick={allowHidingLines ? () => setHiddenLines((state) => state.includes(String(line.id)) ? state.filter((item) => item !== line.id) : [...state, String(line.id)]) : undefined}
            style={allowHidingLines ? { cursor: 'pointer' } : { cursor: 'default', backgroundColor: 'rgba(0, 0, 0, 0)' }}
          >
            <svg width="20" height="3">
              <LegendLineShape
                x={0}
                y={-10}
                size={20}
                fill={hiddenLines.includes(line.id) ? "rgba(1, 1, 1, .1)" : line.color}
                id={line.id}
              />
            </svg>
            <br />
            <span>{line.id}</span>
          </div>
        ))}
      </div>
    );
  };

  // Empty chart if there is no data
  if (!lines || !lines.length) {
    return (
      <React.Fragment>
        <Card>
          <CardContent className={classes.content}>
            <div className={classes.inlineTitle}>
              <Typography className={classes.title} gutterBottom variant="h3">
                {title}
              </Typography>
              <div className={classes.infoButton}>
                <InfoButton onClick={handleModalOpen} />
              </div>
            </div>
            <Divider />
            <div style={{ height: chartHeight }} />
          </CardContent>
        </Card>

        <ChartInfoModal open={modalOpen} onClose={handleModalClose} />
      </React.Fragment>
    );
  }

  const handleMouseMove = point => {
    if (String(point.serieId).includes("Vert") || !allowHoverTooltip) {
      setShowCrosshair(false);
      return;
    }
    setShowCrosshair(true);
  };

  const standardCrosshairTheme = {
    crosshair: {
      line: {
        stroke: '#000000',
        strokeWidth: 1,
        strokeOpacity: 0.75,
      },
    },
  };

  const hiddenCrosshairTheme = {
    crosshair: {
      line: {
        stroke: 'rgba(0, 0, 0, 0)',
        strokeWidth: 0,
        strokeOpacity: 0,
      },
    },
  };

  return (
    <React.Fragment>
      <Card>
        <CardContent className={classes.content}>
          <div className={classes.inlineTitle}>
            <Typography className={classes.title} gutterBottom variant="h3">
              {title}
            </Typography>
            <div className={classes.infoButton}>
              <InfoButton onClick={handleModalOpen} />
            </div>
          </div>
          <Divider />
          <div style={{ height: chartHeight }}>
            <ResponsiveLine
              data={safePlottableData}
              xScale={{
                type: "linear",
                min: minInterval,
                max: maxInterval,
              }}
              yScale={{
                type: "linear",
                stacked: false,
                min: 0,
                max: maxYValue * 1.1,
              }}
              layers={[
                "grid",
                "markers",
                "axes",
                "areas",
                "crosshair",
                "line",
                "slices",
                "points",
                "mesh",
                "legends",
                RectLayer,
                DashedSolidLine,
                VerticalLines,
                D0D2Marker,
              ]}
              theme={showCrosshair ? standardCrosshairTheme : hiddenCrosshairTheme}
              margin={{ top: 20, right: 10, bottom: 50, left: 55 }}
              axisTop={null}
              axisLeft={{
                orient: "left",
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: "Defects",
                legendOffset: -45,
                format: (value) => `${new Intl.NumberFormat('en-US').format(value)}`,
              }}
              axisRight={null}
              axisBottom={{
                orient: "bottom",
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: xAxisLabel || "Interval",
                legendOffset: 40,
                format: (value) => Math.round(value),
              }}
              useMesh={true}
              enableCrosshair={true}
              crosshairType={crosshairType}
              enablePoints={false}
              onMouseMove={handleMouseMove}
              tooltip={({ point }) => {
                if (String(point.serieId).includes("Vert") || !allowHoverTooltip) {
                  return (
                    <div style={{ background: "rgba(255, 255, 255, 0.5)" }}></div>
                  );
                }

                // Format the date if it exists
                const dateDisplay = point.data.date
                  ? new Date(point.data.date).toLocaleDateString("en-US", {
                      month: "2-digit",
                      day: "2-digit",
                      year: "numeric",
                    })
                  : null;

                return (
                  <div
                    style={{
                      background: "white",
                      padding: "10px",
                      border: "1px solid #ccc",
                    }}
                  >
                    Interval: {point.data.x}
                    {dateDisplay && (
                      <>
                        <br />
                        Date: {dateDisplay}
                      </>
                    )}
                    <br />
                    {point.serieId}: {Math.round(point.data.y)}
                  </div>
                );
              }}
              colors={{ datum: "color" }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CustomLegend legendData={legendData} />
          </div>
        </CardContent>
      </Card>

      <ChartInfoModal open={modalOpen} onClose={handleModalClose} />
    </React.Fragment>
  );
}
